// Dev-mode view of diagnostics.ts: the last few one-second samples and every event of the
// current recording, plus copy buttons for this session and for the previous one — the run
// that crashed the renderer, whose console died with it but whose samples were kept in
// localStorage.
import { useEffect, useRef, useState } from "react";
import { Button, Card } from "@grackle/ui";
import { formatSample, recDiag, sessionText, type DiagSession } from "./diagnostics";

const VISIBLE_SAMPLES = 8;
const COPIED_MS = 2000;

function CopySessionButton({ label, session }: { label: string; session: DiagSession }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  async function copy() {
    let next: "copied" | "failed" = "copied";
    try {
      await navigator.clipboard.writeText(sessionText(session));
    } catch {
      next = "failed";
    }
    setState(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState("idle"), COPIED_MS);
  }
  return (
    <Button variant="secondary" size="sm" icon={state === "copied" ? "check" : "copy"} onClick={() => void copy()}>
      {state === "copied" ? "Copied" : state === "failed" ? "Couldn't copy" : label}
    </Button>
  );
}

export default function RecorderDiagnosticsPanel() {
  const [, setVersion] = useState(0);
  useEffect(() => recDiag.subscribe(() => setVersion((v) => v + 1)), []);

  const session = recDiag.current();
  const previous = recDiag.previousSession();
  if (!session && !previous) return null;

  return (
    <Card size="sm" className="publish-status-panel rec-diag-panel">
      <div className="gk-overline">Recorder diagnostics (dev)</div>
      {session ? (
        <>
          <pre className="rec-diag-panel__log">
            {session.samples.length
              ? session.samples.slice(-VISIBLE_SAMPLES).map(formatSample).join("\n")
              : "Waiting for the first sample…"}
          </pre>
          {session.events.length ? <pre className="rec-diag-panel__log">{session.events.join("\n")}</pre> : null}
        </>
      ) : null}
      <div className="rec-diag-panel__actions">
        {session ? <CopySessionButton label="Copy log" session={session} /> : null}
        {previous ? <CopySessionButton label="Copy previous session" session={previous} /> : null}
      </div>
    </Card>
  );
}
